import axios from 'axios';
import { CURRENT_SEASON, SEASONS, DEFAULT_SEASON } from '../config/seasons';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
});

const ADMIN_KEY_STORAGE = 'leagueapp.adminKey';

export const setAdminKey = (key) => {
  try { window.sessionStorage.setItem(ADMIN_KEY_STORAGE, key); } catch (e) { /* private mode */ }
};
export const clearAdminKey = () => {
  try { window.sessionStorage.removeItem(ADMIN_KEY_STORAGE); } catch (e) { /* private mode */ }
};
export const getAdminKey = () => {
  try { return window.sessionStorage.getItem(ADMIN_KEY_STORAGE) || ''; } catch (e) { return ''; }
};

// Attach the admin key to the guarded endpoints only.
api.interceptors.request.use((config) => {
  const url = config.url || '';
  // /admin/* is guarded on every method, reads included — the venue list exposes
  // geocode errors and confidence grades, which is operational detail.
  const guarded =
    url.startsWith('/admin') ||
    (url.startsWith('/overrides') && (
      config.method === 'post' || config.method === 'delete' || url.includes('/verify')
    ));
  if (guarded) {
    const key = getAdminKey();
    if (key) config.headers['X-Admin-Key'] = key;
  }
  return config;
});

// Validate a key before letting the operator into the admin screen.
export async function verifyAdminKey(key) {
  try {
    const response = await api.get('/overrides/verify', { headers: { 'X-Admin-Key': key } });
    return { ok: Boolean(response.data?.ok) };
  } catch (e) {
    const status = e?.response?.status;
    if (status === 503) return { ok: false, reason: 'ADMIN_API_KEY is not configured on the server.' };
    return { ok: false, reason: 'That key was not accepted.' };
  }
}

export async function getGames(ageGroups, conferenceId, leagueDivisionId, season = DEFAULT_SEASON, teamIds, offset = 0, limit = 50, competition = "League", dateWindow = {}) {
  let params = new URLSearchParams();
  
  // Properly append array parameters
  if (ageGroups && ageGroups.length) {
    ageGroups.forEach(a => params.append('age_group', a));
  }
  if (conferenceId) params.append('conference', conferenceId);
  if (leagueDivisionId) params.append('division', leagueDivisionId);
  if (season) params.append('season', season);
  if (teamIds && teamIds.length) {
    teamIds.forEach(t => params.append('team', t));
  }
  if (competition) params.append('competition', competition);
  // Narrow to a date window so mid-season views don't load the whole season.
  if (dateWindow && dateWindow.startDate) params.append('start_date', dateWindow.startDate);
  if (dateWindow && dateWindow.endDate) params.append('end_date', dateWindow.endDate);
  params.append('offset', offset);
  params.append('limit', limit);

  const response = await api.get('/games', { params });
  
  return response.data;
}

// The standings endpoint returns snake_case names; the Standings table renders
// short column keys (PTS, GD, W...). Those used to be sent twice, once under each
// name — verified identical across 7,496 rows, and 14% of the payload. The API now
// sends one set and this maps it, so the table's ~100 references to the short keys
// keep working and the wire format stays lean.
const COLUMN_ALIASES = {
  team_name: 'team',
  wins: 'W',
  losses: 'L',
  ties: 'D',
  goals_for: 'GF',
  goals_against: 'GA',
  goal_difference: 'GD',
  points: 'PTS',
};

function withColumnAliases(row) {
  const out = { ...row };
  for (const [source, alias] of Object.entries(COLUMN_ALIASES)) {
    if (row[source] !== undefined) out[alias] = row[source];
  }
  return out;
}

export const getStandings = async (
  ageGroup,
  conferenceId,
  leagueDivisionId,
  season = DEFAULT_SEASON,
  competition = "League"
) => {
  const params = { age_group: ageGroup, season: season };
  
  if (conferenceId && conferenceId !== 'all') params.conference_id = conferenceId;
  if (leagueDivisionId && leagueDivisionId !== 'all') params.division_id = leagueDivisionId;
  if (competition) params.competition = competition;
  
  const response = await api.get('/standings', { params });
  return Array.isArray(response.data) ? response.data.map(withColumnAliases) : response.data;
};

// Conference names differ between competitions — League's "Mid-Atlantic" is
// "Mid-Atlantic (North)" and "(South)" in Flex — so the list has to be fetched
// for the competition being viewed. Pass it, or the caller gets League's names
// and a conference that resolves to nothing on the Standings table.
export const getConferences = async (season = DEFAULT_SEASON, divisionId = null, competition = null) => {
  let url = `/divisions/conferences?season=${season}`;
  if (divisionId) {
    url += `&division_id=${divisionId}`;
  }
  if (competition) {
    url += `&competition=${encodeURIComponent(competition)}`;
  }
  const response = await api.get(url);
  return response.data;
};

export const getConferencesByDivision = async (divisionId, season = DEFAULT_SEASON, competition = null) => {
  const response = await api.get(`/divisions/${divisionId}/conferences`, {
    params: competition ? { season, competition } : { season }
  });
  return response.data;
};

export const getLeagueDivisions = async (season = DEFAULT_SEASON) => {
  const response = await api.get(`/divisions?season=${season}`);
  return response.data;
};

// Legacy function for backward compatibility
export const getDivisions = async (season = DEFAULT_SEASON) => {
  const response = await api.get(`/divisions?season=${season}`);
  return response.data;
};

export const getTeams = async (
  ageGroups = ['U13'],
  division,
  season = DEFAULT_SEASON
) => {
  let params = new URLSearchParams();
  
  // Properly append array parameters
  if (ageGroups && ageGroups.length) {
    ageGroups.forEach(a => params.append('age_groups', a));
  }
  if (season) params.append('season', season);
  if (division) params.append('division', division);
  
  const response = await api.get('/teams', { params });
  return response.data;
};

export const getQoPHistory = async (
  ageGroup,
  conferenceId,
  divisionId,
  season = DEFAULT_SEASON,
  competition = "League"
) => {
  const params = { age_group: ageGroup, season: season };

  if (conferenceId && conferenceId !== 'all') params.conference_id = conferenceId;
  if (divisionId && divisionId !== 'all') params.division_id = divisionId;
  if (competition) params.competition = competition;

  const response = await api.get('/qop/history', { params });
  return response.data;
};

export const getTeamDetail = async (teamId) => {
  const response = await api.get(`/teams/${teamId}`);
  return response.data;
};

// --- Admin Override API ---

export const getOverrides = async () => {
  const response = await api.get('/overrides');
  return response.data;
};

export const searchGamesForOverride = async (dateFrom, dateTo, ageGroups, teamName) => {
  let params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  if (ageGroups && ageGroups.length) {
    ageGroups.forEach(a => params.append('age_group', a));
  }
  if (teamName) params.append('team_name', teamName);
  const response = await api.get('/overrides/games', { params });
  return response.data;
};

export const createOrUpdateOverride = async (matchId, homeScore, awayScore) => {
  const response = await api.post('/overrides', {
    match_id: matchId,
    home_score: homeScore,
    away_score: awayScore,
  });
  return response.data;
};

export const deleteOverride = async (matchId) => {
  const response = await api.delete(`/overrides/${encodeURIComponent(matchId)}`);
  return response.data;
};

export async function getNextGameOffset(ageGroups, conferenceId, divisionId, season = DEFAULT_SEASON, teamIds, competition = "League") {
  let params = new URLSearchParams();
  
  if (ageGroups && ageGroups.length) {
    ageGroups.forEach(a => params.append('age_group', a));
  }
  if (conferenceId) params.append('conference', conferenceId);
  if (divisionId) params.append('division', divisionId);
  if (season) params.append('season', season);
  if (teamIds && teamIds.length) {
    teamIds.forEach(t => params.append('team', t));
  }
  if (competition) params.append('competition', competition);

  const response = await api.get('/games/next', { params });
  return response.data;
}

// Seasons the backend has data for, newest first. Falls back to the build-time
// SEASONS list if the endpoint is unavailable so the dropdown is never empty.
export async function fetchSeasons() {
  try {
    const response = await api.get('/seasons');
    return response.data;
  } catch (e) {
    return { seasons: SEASONS, current_season: CURRENT_SEASON };
  }
}

// Last successful scrape, so data staleness is visible in the app rather than only
// in the pipeline_runs table.
export async function fetchPipelineHealth() {
  try {
    const response = await api.get('/health/pipeline');
    return response.data;
  } catch (e) {
    return null;
  }
}

// Game-time weather for specific fixtures, keyed by match_id.
//
// Deliberately a second request rather than part of /games: the fixture list is
// cached for ten minutes and shared by every viewer, while a forecast only
// matters for the handful of games inside the NWS horizon that someone is
// actually looking at. Failure is silent — a fixture with no forecast simply
// shows no weather.
export async function getWeather(matchIds) {
  const ids = (matchIds || []).filter(Boolean);
  if (!ids.length) return {};
  try {
    const params = new URLSearchParams();
    ids.slice(0, 120).forEach(id => params.append('match_id', id));
    const response = await api.get(`/weather?${params.toString()}`);
    return response.data?.weather || {};
  } catch (e) {
    return {};
  }
}


// --- Venue curation (admin) ---
//
// Venue coordinates gate the driving distance and kickoff weather, and only
// high/manual confidence venues are ever served, so curating them is the highest
// leverage data work available.

export async function getCurationVenues() {
  const response = await api.get('/admin/venues');
  return response.data;
}

/**
 * Place search, proxied through our API.
 *
 * Results carry a `provider`. Google returns a place id and needs a second lookup
 * for the coordinate; Nominatim returns lat/lon directly.
 */
export async function searchPlaces(query) {
  const response = await api.get('/admin/geocode', { params: { q: query } });
  return response.data;
}

/** Resolve a Google place id to a coordinate. */
export async function resolvePlace(placeId) {
  const response = await api.get('/admin/geocode/place', { params: { id: placeId } });
  return response.data;
}

/** Place a venue by hand. Marks it manual, which the geocoder then leaves alone. */
export async function setVenueLocation(id, latitude, longitude, note) {
  const response = await api.put(`/admin/venues/${id}/location`, {
    latitude, longitude, note: note || null,
  });
  return response.data;
}

/** Withdraw a location, returning the venue to the unresolved pool. */
export async function clearVenueLocation(id) {
  const response = await api.delete(`/admin/venues/${id}/location`);
  return response.data;
}

// ---- venues (public) -------------------------------------------------------
// A venue page is keyed on a venue id but shows the whole *site*: the server
// gathers every venue row within a few hundred feet, because a quarter of
// located venues share a site with another row and keying on the row alone
// splits one complex across two half-empty pages.

export async function getVenue(id) {
  const res = await api.get(`/venues/${id}`);
  return res.data;
}

export async function getVenueDates(id) {
  const res = await api.get(`/venues/${id}/dates`);
  return res.data?.dates || [];
}

export async function getVenueGames(id, date) {
  const res = await api.get(`/venues/${id}/games`, { params: { date } });
  return res.data;
}

/**
 * The hour-by-hour strip around one fixture's kickoff.
 *
 * Its own request rather than a field on getWeather: a schedule page asks about
 * up to 120 fixtures and almost none get hovered. Returns null when the fixture
 * has no stored forecast, which is the normal case beyond seven days.
 */
export async function getHourlyWeather(matchId) {
  try {
    const res = await api.get('/weather/hourly', { params: { match_id: matchId } });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}

